/**
 * GigSearchPage - Browse freelance gigs with contextual service nudges
 * Shows inline service prompts based on user behavior
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { 
  Search, Briefcase, MapPin, Clock, DollarSign, Star, 
  Filter, ChevronRight, CheckCircle, ArrowRight, X,
  Sparkles, Target, FileText, Users
} from 'lucide-react';
import FreelanceServicesSection from '../components/FreelanceServicesSection';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline Nudge Component
const ServiceNudge = ({ nudge, onDismiss, onAction }) => {
  if (!nudge) return null;
  
  return (
    <div 
      className="relative flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-sm animate-in fade-in slide-in-from-bottom-2"
      data-testid="service-nudge"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <span className="text-xl">{nudge.icon}</span>
        </div>
        <div>
          <p className="font-medium text-gray-900">{nudge.message}</p>
          <p className="text-sm text-gray-500">{nudge.subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={() => onAction(nudge)} className="gap-1">
          {nudge.cta}
          <ArrowRight className="w-4 h-4" />
        </Button>
        <button 
          onClick={onDismiss}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

// Gig Card Component
const GigCard = ({ gig, onApply, onView }) => {
  return (
    <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer group" onClick={onView}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                {gig.title}
              </h3>
              <p className="text-sm text-gray-500">{gig.company || 'Client'}</p>
            </div>
          </div>
          {gig.featured && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Featured</Badge>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{gig.description}</p>

        <div className="flex flex-wrap gap-2 mb-3">
          {gig.skills?.slice(0, 3).map((skill, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {skill}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {gig.budget && (
              <span className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                {gig.budget}
              </span>
            )}
            {gig.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {gig.location}
              </span>
            )}
            {gig.duration && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {gig.duration}
              </span>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onApply(gig); }}>
            Apply
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Footer
const MobileStickyFooter = ({ onOpen }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg md:hidden z-40">
      <button
        onClick={onOpen}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full"
      >
        <div className="flex items-center gap-2">
          <span>🧑‍💻</span>
          <span className="font-medium">Freelance Help</span>
        </div>
        <span className="text-sm opacity-90">From £35</span>
      </button>
    </div>
  );
};

const GigSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [subcategories, setSubcategories] = useState([]);
  const [services, setServices] = useState(null);
  const [triggers, setTriggers] = useState({});
  
  // Nudge state
  const [viewCount, setViewCount] = useState(0);
  const [appliedCount, setAppliedCount] = useState(0);
  const [currentNudge, setCurrentNudge] = useState(null);
  const [dismissedNudges, setDismissedNudges] = useState([]);
  const [showServiceSheet, setShowServiceSheet] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Fetch data
  useEffect(() => {
    fetchGigs();
    fetchServices();
    fetchSubcategories();
  }, []);

  const fetchGigs = async () => {
    try {
      setLoading(true);
      
      // Sample gigs for demonstration (always show for demo)
      const sampleGigs = [
        {
          id: '1',
          title: 'React Developer for E-commerce Project',
          company: 'TechStartup Ltd',
          description: 'Looking for an experienced React developer to build a modern e-commerce platform. Must have experience with Next.js and Tailwind CSS.',
          skills: ['React', 'Next.js', 'Tailwind'],
          budget: '£2,000 - £3,500',
          location: 'Remote',
          duration: '2-3 weeks',
          featured: true,
        },
        {
          id: '2',
          title: 'Logo & Brand Identity Designer',
          company: 'New Brand Co',
          description: 'Need a creative designer to develop complete brand identity including logo, color palette, and brand guidelines.',
          skills: ['Logo Design', 'Branding', 'Illustrator'],
          budget: '£500 - £800',
          location: 'Remote',
          duration: '1 week',
        },
        {
          id: '3',
          title: 'Content Writer for Tech Blog',
          company: 'Digital Media Inc',
          description: 'Seeking an SEO-savvy content writer to produce 10 articles per month on AI and technology topics.',
          skills: ['SEO Writing', 'Tech', 'Research'],
          budget: '£50/article',
          location: 'Remote',
          duration: 'Ongoing',
        },
        {
          id: '4',
          title: 'Virtual Assistant for Executive',
          company: 'CEO Services',
          description: 'Part-time VA needed for calendar management, email handling, and travel booking. Must be organized and detail-oriented.',
          skills: ['Calendar', 'Email', 'Travel'],
          budget: '£15-20/hr',
          location: 'Remote',
          duration: '15 hrs/week',
        },
        {
          id: '5',
          title: 'Social Media Marketing Specialist',
          company: 'Growth Agency',
          description: 'Create and manage social media campaigns for our clients. Experience with paid ads and analytics required.',
          skills: ['Social Media', 'Ads', 'Analytics'],
          budget: '£1,500/month',
          location: 'Remote',
          duration: '3 months',
        },
        {
          id: '6',
          title: 'Video Editor for YouTube Channel',
          company: 'Content Creator',
          description: 'Edit weekly vlogs and tutorial videos. Must be proficient in Premiere Pro or Final Cut Pro.',
          skills: ['Premiere Pro', 'YouTube', 'Motion Graphics'],
          budget: '£100-150/video',
          location: 'Remote',
          duration: 'Ongoing',
        },
      ];
      
      // Try to fetch real gigs first
      try {
        const response = await fetch(`${API_URL}/api/jobs?category=freelance-gigs&limit=20`);
        const data = await response.json();
        
        if (data.jobs?.length > 0) {
          setGigs(data.jobs);
        } else {
          setGigs(sampleGigs);
        }
      } catch {
        // Fallback to sample data
        setGigs(sampleGigs);
      }
    } catch (error) {
      console.error('Error fetching gigs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/job-services/services/freelance-gigs`);
      const data = await response.json();
      if (data.success) {
        setServices(data);
        setTriggers(data.triggers || {});
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/job-services/subcategories/freelance-gigs`);
      const data = await response.json();
      if (data.success && data.has_subcategories) {
        setSubcategories(data.subcategories || []);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  // Trigger nudges based on user actions
  const checkForNudge = useCallback((action, context = {}) => {
    // Don't show if already dismissed
    if (dismissedNudges.includes(action)) return;
    
    const nudgeConfigs = {
      browse_5_gigs: {
        trigger: viewCount >= 5,
        icon: '🧑‍💻',
        message: 'Clients compare profiles fast.',
        subtitle: 'A polished profile can increase your response rate by 40%',
        cta: 'Improve my chances',
        serviceId: 'profile_optimisation',
        price: 35,
      },
      first_apply: {
        trigger: appliedCount === 1,
        icon: '✨',
        message: 'Great! You applied to your first gig.',
        subtitle: 'Make sure your profile stands out from other applicants',
        cta: 'Optimise profile',
        serviceId: 'profile_optimisation',
        price: 35,
      },
      portfolio_gig: {
        trigger: context.requiresPortfolio,
        icon: '🖼',
        message: 'This gig requires a portfolio.',
        subtitle: 'A strong portfolio can make or break your application',
        cta: 'Review portfolio',
        serviceId: 'portfolio_review',
        price: 40,
      },
      fixed_budget: {
        trigger: context.hasFixedBudget,
        icon: '💬',
        message: 'Fixed budget gigs need strong proposals.',
        subtitle: 'Learn to price and pitch with confidence',
        cta: 'Improve proposals',
        serviceId: 'proposal_coaching',
        price: 45,
      },
      multiple_rejections: {
        trigger: appliedCount >= 3 && context.noResponses,
        icon: '📊',
        message: 'Not getting responses?',
        subtitle: 'Your approach might need some tweaking',
        cta: 'Get feedback',
        serviceId: 'portfolio_review',
        price: 40,
      },
    };

    // Check each nudge in priority order
    for (const [key, config] of Object.entries(nudgeConfigs)) {
      if (config.trigger && !dismissedNudges.includes(key)) {
        setCurrentNudge({ key, ...config });
        break;
      }
    }
  }, [viewCount, appliedCount, dismissedNudges]);

  // Track gig views
  const handleViewGig = (gig) => {
    setViewCount(prev => prev + 1);
    
    // Check for portfolio requirement
    const requiresPortfolio = gig.skills?.some(s => 
      ['design', 'portfolio', 'creative', 'video'].some(k => s.toLowerCase().includes(k))
    );
    
    // Check for fixed budget
    const hasFixedBudget = gig.budget && !gig.budget.includes('/hr');
    
    setTimeout(() => {
      checkForNudge('view', { requiresPortfolio, hasFixedBudget });
    }, 500);
    
    // Navigate to gig detail (or show modal)
    toast({
      title: gig.title,
      description: 'Viewing gig details...',
    });
  };

  // Track applications
  const handleApply = (gig) => {
    setAppliedCount(prev => prev + 1);
    
    setTimeout(() => {
      checkForNudge('apply', {});
    }, 1000);
    
    toast({
      title: 'Application Started',
      description: `Applying to: ${gig.title}`,
    });
  };

  // Dismiss nudge
  const handleDismissNudge = () => {
    if (currentNudge) {
      setDismissedNudges(prev => [...prev, currentNudge.key]);
    }
    setCurrentNudge(null);
  };

  // Handle nudge action
  const handleNudgeAction = (nudge) => {
    setSelectedService(nudge.serviceId);
    setShowServiceSheet(true);
    handleDismissNudge();
  };

  // Check for browse nudge
  useEffect(() => {
    if (viewCount === 5) {
      checkForNudge('browse_5_gigs');
    }
  }, [viewCount, checkForNudge]);

  // Filter gigs
  const filteredGigs = gigs.filter(gig => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return gig.title?.toLowerCase().includes(q) || 
             gig.description?.toLowerCase().includes(q) ||
             gig.skills?.some(s => s.toLowerCase().includes(q));
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="gig-search-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            💼 Freelance & Gigs
          </h1>
          <p className="text-blue-100 mb-6">
            Find projects that match your skills. Stand out with professional preparation.
          </p>
          
          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search gigs by skill, keyword, or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white text-gray-900 border-0 h-12"
                data-testid="gig-search-input"
              />
            </div>
            <Button variant="secondary" className="h-12 px-6 bg-white text-blue-600 hover:bg-blue-50">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Subcategory Tabs */}
        {subcategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="subcategory-tabs">
            <Button
              size="sm"
              variant={selectedSubcategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedSubcategory('all')}
            >
              All Gigs
            </Button>
            {subcategories.map((sub) => (
              <Button
                key={sub.id}
                size="sm"
                variant={selectedSubcategory === sub.id ? 'default' : 'outline'}
                onClick={() => setSelectedSubcategory(sub.id)}
              >
                {sub.icon} {sub.name}
              </Button>
            ))}
          </div>
        )}

        {/* Service Nudge */}
        {currentNudge && (
          <div className="mb-6">
            <ServiceNudge
              nudge={currentNudge}
              onDismiss={handleDismissNudge}
              onAction={handleNudgeAction}
            />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Gig List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">
                {filteredGigs.length} gigs found
                {viewCount > 0 && <span className="ml-2 text-blue-600">• {viewCount} viewed</span>}
              </p>
              <Select defaultValue="recent">
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="budget-high">Budget: High to Low</SelectItem>
                  <SelectItem value="budget-low">Budget: Low to High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-40 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {filteredGigs.map((gig, index) => (
                  <React.Fragment key={gig.id}>
                    <GigCard
                      gig={gig}
                      onView={() => handleViewGig(gig)}
                      onApply={() => handleApply(gig)}
                    />
                    
                    {/* Inline service prompt after 3 gigs */}
                    {index === 2 && !dismissedNudges.includes('inline_after_3') && (
                      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Stand out to clients</p>
                                <p className="text-sm text-gray-500">Profile Optimisation · From £35</p>
                              </div>
                            </div>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedService('profile_optimisation');
                                setShowServiceSheet(true);
                              }}
                            >
                              Improve chances
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          {/* Sidebar - Services */}
          <div className="hidden lg:block">
            <div className="sticky top-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Boost Your Success
                  </CardTitle>
                  <CardDescription>Stand out from other freelancers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { id: 'profile_optimisation', icon: '🧑‍💻', name: 'Profile Optimisation', price: 35, badge: 'Popular' },
                    { id: 'portfolio_review', icon: '🖼', name: 'Portfolio Review', price: 40 },
                    { id: 'proposal_coaching', icon: '💬', name: 'Proposal Coaching', price: 45 },
                  ].map((service) => (
                    <button
                      key={service.id}
                      onClick={() => {
                        setSelectedService(service.id);
                        setShowServiceSheet(true);
                      }}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{service.icon}</span>
                        <div>
                          <p className="font-medium text-sm">{service.name}</p>
                          <p className="text-xs text-gray-500">From £{service.price}</p>
                        </div>
                      </div>
                      {service.badge && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          {service.badge}
                        </Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Star className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-emerald-800">Get Badges</p>
                      <p className="text-xs text-emerald-600">Show clients you're prepared</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Badge className="bg-green-100 text-green-700 border-green-200">🟢 Profile Optimised</Badge>
                    <Badge className="bg-green-100 text-green-700 border-green-200">🟢 Portfolio Reviewed</Badge>
                    <Badge className="bg-green-100 text-green-700 border-green-200">🟢 Pricing Ready</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Footer */}
      <MobileStickyFooter onOpen={() => setShowServiceSheet(true)} />

      {/* Service Sheet */}
      <Sheet open={showServiceSheet} onOpenChange={setShowServiceSheet}>
        <SheetContent side="bottom" className="h-[80vh] rounded-t-2xl">
          <SheetHeader className="text-left pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              💼 Freelance Services
            </SheetTitle>
          </SheetHeader>
          <div className="py-4 overflow-y-auto">
            <FreelanceServicesSection
              placement="detail"
              showMobileSticky={false}
              onServiceSelect={(service) => {
                toast({
                  title: 'Service Selected',
                  description: `Starting ${service.name}...`,
                });
                setShowServiceSheet(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default GigSearchPage;
